import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileSpreadsheet, Mail, ListChecks, Plus, Pencil, Trash2,
  Send, PlayCircle, CheckCircle2, AlertCircle, FileText, Settings2,
} from "lucide-react";
import { toast } from "sonner";

const TRIGGER_LABELS = {
  policy_expiry: "Scadenza polizza",
  client_welcome: "Benvenuto nuovo cliente",
  payment_confirm: "Conferma pagamento ricevuto",
};

const TRIGGER_DESC = {
  policy_expiry: "Inviata in automatico X giorni prima della scadenza di ogni polizza attiva.",
  client_welcome: "Inviata appena l'agente abilita l'area riservata per un nuovo cliente.",
  payment_confirm: "Inviata appena un pagamento online viene marcato come ricevuto.",
};

const TRIGGER_VARS = {
  policy_expiry: [
    "nome_cliente", "nome", "cognome", "email_cliente", "telefono",
    "numero_polizza", "tipo_polizza", "compagnia", "targa", "scadenza", "premio", "giorni_alla_scadenza",
  ],
  client_welcome: [
    "nome_cliente", "nome", "cognome", "email_cliente", "telefono",
    "password_temporanea", "link_area_riservata",
  ],
  payment_confirm: [
    "nome_cliente", "nome", "cognome", "email_cliente",
    "numero_polizza", "tipo_polizza", "compagnia", "importo_pagato", "metodo_pagamento",
  ],
};

const DEFAULT_TEMPLATES = {
  policy_expiry: {
    name: "Promemoria scadenza polizza",
    subject_template: "⏰ La tua polizza {numero_polizza} scade tra {giorni_alla_scadenza} giorni",
    body_template:
      "Ciao {nome_cliente},\n\nLa tua polizza {tipo_polizza} {numero_polizza} ({compagnia}) scadrà il {scadenza}.\n\n" +
      "Contattaci per il rinnovo prima della scadenza.\n\nA presto,\nIl team Assicura",
    offset_days: 30,
  },
  client_welcome: {
    name: "Benvenuto in Assicura",
    subject_template: "👋 Benvenuto in Assicura, {nome}!",
    body_template:
      "Ciao {nome_cliente},\n\nTi diamo il benvenuto nell'area riservata della nostra agenzia.\n\n" +
      "Le tue credenziali di accesso:\n• Email: {email_cliente}\n• Password temporanea: {password_temporanea}\n\n" +
      "Accedi qui: {link_area_riservata}\n\nA presto!",
  },
  payment_confirm: {
    name: "Conferma pagamento ricevuto",
    subject_template: "✅ Pagamento ricevuto — {numero_polizza}",
    body_template:
      "Ciao {nome_cliente},\n\nConfermiamo la ricezione del pagamento di €{importo_pagato} per la polizza " +
      "{numero_polizza} ({compagnia}) tramite {metodo_pagamento}.\n\nGrazie per la fiducia.",
  },
};

const emptyAutomation = {
  name: "",
  trigger_type: "policy_expiry",
  offset_days: 30,
  subject_template: "",
  body_template: "",
  enabled: true,
};

// ------------------------ EXCEL IMPORT TAB ------------------------
const ExcelImportTab = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [report, setReport] = useState(null);
  const [imports, setImports] = useState([]);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const loadImports = useCallback(async () => {
    try {
      const { data } = await api.get("/automations/excel/imports");
      setImports(data || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  const onPickFile = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      toast.error("Carica un file Excel (.xlsx)");
      return;
    }
    setFile(f);
    setPreview(null);
    setReport(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/automations/excel/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.auto_mapping || {});
    } catch (e) {
      toast.error(formatApiError(e));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!file || !preview) return;
    setBusy(true);
    setReport(null);
    try {
      const payload = {
        mapping: Object.fromEntries(
          Object.entries(mapping).filter(([, v]) => v !== "" && v !== null && v !== undefined),
        ),
        has_header: true,
        dry_run: !!dryRun,
        update_existing: !!updateExisting,
      };
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/automations/excel/import?payload=${encodeURIComponent(JSON.stringify(payload))}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setReport(data);
      toast.success(
        dryRun
          ? `Anteprima completata: ${data.clients_created} clienti, ${data.policies_created} polizze`
          : `Import completato: +${data.clients_created} clienti, +${data.policies_created} polizze`,
      );
      if (!dryRun) loadImports();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <FileSpreadsheet className="h-6 w-6 text-emerald-600 mt-1" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Import Database Excel</h2>
            <p className="text-sm text-slate-500">
              Importa clienti e polizze da un file .xlsx. Vengono saltati i duplicati (CF cliente o n° polizza già esistenti).
            </p>
          </div>
        </div>

        {!preview && (
          <div
            data-testid="excel-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); onPickFile(e.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg py-12 cursor-pointer transition-colors ${
              drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400 hover:bg-slate-50"
            }`}
          >
            <Upload className="h-10 w-10 text-slate-400 mb-3" />
            <p className="font-medium text-slate-700">Trascina qui il file Excel o clicca per selezionarlo</p>
            <p className="text-xs text-slate-500 mt-1">Formati supportati: .xlsx</p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              data-testid="excel-file-input"
            />
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-sm">
                <span className="font-medium text-slate-900">{file?.name}</span>
                <span className="text-slate-500 ml-2">· {preview.total_rows} righe · {preview.headers.length} colonne</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFile(null); setPreview(null); setReport(null); setMapping({}); }}
                data-testid="excel-reset-btn"
              >
                Cambia file
              </Button>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-slate-900 mb-2">Mappa le colonne</h3>
              <p className="text-xs text-slate-500 mb-3">
                Associa ogni campo del CRM alla colonna corrispondente nel tuo Excel. Le associazioni rilevate automaticamente sono già pre-impostate.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(preview.fields).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Label className="min-w-[140px] text-xs text-slate-600">{label}</Label>
                    <Select
                      value={mapping[key] !== undefined && mapping[key] !== null ? String(mapping[key]) : "__none__"}
                      onValueChange={(v) =>
                        setMapping((m) => {
                          const next = { ...m };
                          if (v === "__none__") delete next[key];
                          else next[key] = parseInt(v, 10);
                          return next;
                        })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`excel-map-${key}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Nessuna colonna —</SelectItem>
                        {preview.headers.map((h, i) => (
                          <SelectItem key={i} value={String(i)}>{h || `Col ${i + 1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">Anteprima primi 5 record</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-white border-b border-slate-200">
                    <tr>
                      {preview.headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-slate-100">
                        {row.map((c, ci) => (
                          <td key={ci} className="px-3 py-2 text-slate-700 whitespace-nowrap">{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3 bg-amber-50">
              <div className="flex items-center gap-2">
                <Switch
                  id="update-existing"
                  checked={updateExisting}
                  onCheckedChange={setUpdateExisting}
                  data-testid="excel-update-existing-switch"
                />
                <Label htmlFor="update-existing" className="text-sm">
                  Aggiorna i clienti già esistenti (match per CF)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" disabled={busy} onClick={() => runImport(true)} data-testid="excel-dryrun-btn">
                  <PlayCircle className="h-4 w-4 mr-1" /> Anteprima (dry-run)
                </Button>
                <Button disabled={busy} onClick={() => runImport(false)} data-testid="excel-run-btn">
                  <Upload className="h-4 w-4 mr-1" /> Importa nel database
                </Button>
              </div>
            </div>

            {report && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="excel-report">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-900">
                    {report.dry_run ? "Anteprima completata" : "Import completato"}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-slate-700">
                  <div><span className="text-slate-500">Righe processate:</span> <strong>{report.rows_processed}</strong></div>
                  <div><span className="text-slate-500">Clienti creati:</span> <strong className="text-emerald-700">{report.clients_created}</strong></div>
                  <div><span className="text-slate-500">Clienti aggiornati:</span> <strong>{report.clients_updated}</strong></div>
                  <div><span className="text-slate-500">Clienti saltati:</span> <strong>{report.clients_skipped}</strong></div>
                  <div><span className="text-slate-500">Polizze create:</span> <strong className="text-emerald-700">{report.policies_created}</strong></div>
                  <div><span className="text-slate-500">Polizze saltate:</span> <strong>{report.policies_skipped}</strong></div>
                  <div className="col-span-2"><span className="text-slate-500">Errori:</span> <strong>{report.errors.length}</strong></div>
                </div>
                {report.errors.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-rose-700 font-medium">Mostra errori ({report.errors.length})</summary>
                    <ul className="mt-2 text-xs text-rose-700 space-y-1 max-h-40 overflow-y-auto">
                      {report.errors.map((e, i) => <li key={i}>· {e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {imports.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" /> Cronologia import
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">File</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Utente</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Clienti</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Polizze</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Errori</th>
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => (
                  <tr key={imp.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{formatDate(imp.ran_at)}</td>
                    <td className="px-3 py-2 text-slate-700">{imp.filename}</td>
                    <td className="px-3 py-2 text-slate-500">{imp.user_name}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">+{imp.report?.clients_created || 0}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">+{imp.report?.policies_created || 0}</td>
                    <td className="px-3 py-2 text-right">{(imp.report?.errors || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------------ AUTOMATIONS TAB ------------------------
const AutomationsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyAutomation);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/automations");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (trigger) => {
    const tpl = DEFAULT_TEMPLATES[trigger];
    setForm({ ...emptyAutomation, trigger_type: trigger, ...tpl });
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name,
      trigger_type: item.trigger_type,
      offset_days: item.offset_days ?? 30,
      subject_template: item.subject_template,
      body_template: item.body_template,
      enabled: !!item.enabled,
    });
    setEditing(item);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.subject_template.trim() || !form.body_template.trim()) {
      toast.error("Compila nome, oggetto e corpo");
      return;
    }
    try {
      const payload = { ...form };
      if (payload.trigger_type !== "policy_expiry") payload.offset_days = null;
      else payload.offset_days = parseInt(payload.offset_days, 10) || 30;
      if (editing) await api.put(`/automations/${editing.id}`, payload);
      else await api.post("/automations", payload);
      toast.success(editing ? "Automazione aggiornata" : "Automazione creata");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (item) => {
    try {
      await api.post(`/automations/${item.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/automations/${deleteTarget.id}`);
      toast.success("Automazione eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendTest = async (item) => {
    try {
      const { data } = await api.post(`/automations/${item.id}/test`);
      if (data.sent) toast.success(`Email di test inviata a ${data.to}`);
      else toast.error("Invio test fallito (verifica SMTP)");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/automations/run-now");
      toast.success(`Job eseguito: ${data.sent} email inviate su ${data.automations} automazioni attive`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const varsForTrigger = useMemo(() => TRIGGER_VARS[form.trigger_type] || [], [form.trigger_type]);

  const grouped = useMemo(() => {
    const g = { policy_expiry: [], client_welcome: [], payment_confirm: [] };
    for (const it of items) (g[it.trigger_type] || (g[it.trigger_type] = [])).push(it);
    return g;
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Automazioni Email</h2>
          <p className="text-sm text-slate-500">
            Configura email automatiche basate su eventi (scadenza, benvenuto, pagamento). Esecuzione giornaliera alle 09:15.
          </p>
        </div>
        <Button variant="outline" onClick={runNow} disabled={running} data-testid="automation-run-now">
          <PlayCircle className="h-4 w-4 mr-1" /> {running ? "In esecuzione…" : "Esegui ora"}
        </Button>
      </div>

      {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
        <div key={key} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" /> {label}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{TRIGGER_DESC[key]}</p>
            </div>
            <Button size="sm" onClick={() => openCreate(key)} data-testid={`automation-create-${key}`}>
              <Plus className="h-4 w-4 mr-1" /> Nuova
            </Button>
          </div>

          {loading && <div className="text-sm text-slate-400">Caricamento…</div>}

          {!loading && (grouped[key] || []).length === 0 && (
            <div className="text-sm text-slate-400 italic">Nessuna automazione configurata.</div>
          )}

          <div className="space-y-2">
            {(grouped[key] || []).map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between border border-slate-200 rounded-md p-3 hover:bg-slate-50 transition-colors"
                data-testid={`automation-item-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">{it.name}</span>
                    {it.trigger_type === "policy_expiry" && (
                      <Badge variant="secondary" className="text-xs">{it.offset_days} giorni prima</Badge>
                    )}
                    {it.enabled
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Attiva</Badge>
                      : <Badge variant="outline" className="text-xs text-slate-500">Disattivata</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{it.subject_template}</div>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <Switch checked={it.enabled} onCheckedChange={() => toggle(it)} data-testid={`automation-toggle-${it.id}`} />
                  <Button variant="ghost" size="icon" onClick={() => sendTest(it)} title="Invia test" data-testid={`automation-test-${it.id}`}>
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`automation-edit-${it.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(it)} data-testid={`automation-delete-${it.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica automazione" : "Nuova automazione"}</DialogTitle>
            <DialogDescription>{TRIGGER_DESC[form.trigger_type]}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="automation-form-name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Trigger</Label>
                <Select
                  value={form.trigger_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, trigger_type: v }))}
                  disabled={!!editing}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIGGER_LABELS).map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.trigger_type === "policy_expiry" && (
                <div>
                  <Label>Giorni prima della scadenza</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={form.offset_days || 30}
                    onChange={(e) => setForm((f) => ({ ...f, offset_days: parseInt(e.target.value, 10) }))}
                    data-testid="automation-form-offset"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>Oggetto email</Label>
              <Input
                value={form.subject_template}
                onChange={(e) => setForm((f) => ({ ...f, subject_template: e.target.value }))}
                data-testid="automation-form-subject"
              />
            </div>

            <div>
              <Label>Corpo email</Label>
              <Textarea
                rows={8}
                value={form.body_template}
                onChange={(e) => setForm((f) => ({ ...f, body_template: e.target.value }))}
                data-testid="automation-form-body"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="text-xs font-medium text-blue-900 mb-2">Variabili disponibili (clicca per copiare):</div>
              <div className="flex flex-wrap gap-1.5">
                {varsForTrigger.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="text-xs bg-white border border-blue-300 text-blue-700 px-2 py-0.5 rounded hover:bg-blue-100 font-mono"
                    onClick={() => {
                      navigator.clipboard.writeText(`{${v}}`);
                      toast.success(`{${v}} copiato`);
                    }}
                  >
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                id="aut-enabled"
                data-testid="automation-form-enabled"
              />
              <Label htmlFor="aut-enabled">Attiva subito</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="automation-form-save">{editing ? "Salva" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina automazione?</DialogTitle>
            <DialogDescription>
              Stai per eliminare <strong>{deleteTarget?.name}</strong>. L&apos;azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button variant="destructive" onClick={remove} data-testid="automation-delete-confirm">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ------------------------ LOGS TAB ------------------------
const LogsTab = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/automations/runs");
      setRuns(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-slate-500" /> Log esecuzioni
        </h2>
        <Button variant="outline" size="sm" onClick={load}>Aggiorna</Button>
      </div>
      {loading && <div className="text-sm text-slate-400">Caricamento…</div>}
      {!loading && runs.length === 0 && (
        <div className="text-sm text-slate-400 italic">Nessuna esecuzione registrata.</div>
      )}
      {!loading && runs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Quando</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Automazione</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Cliente</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Polizza</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Destinatario</th>
                <th className="px-3 py-2 text-center font-medium text-slate-600">Esito</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatDate(r.ran_at)}</td>
                  <td className="px-3 py-2 text-slate-700">{r.automation_name}</td>
                  <td className="px-3 py-2 text-slate-700">{r.client_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-700">{r.policy_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{r.to_email}</td>
                  <td className="px-3 py-2 text-center">
                    {r.sent
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                      : <AlertCircle className="h-4 w-4 text-rose-600 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------ MAIN PAGE ------------------------
export default function Automations() {
  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-blue-600 text-white flex items-center justify-center">
          <Settings2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight text-slate-900">Gestione Automazioni</h1>
          <p className="text-sm text-slate-500">Import dati Excel + email automatiche basate su eventi.</p>
        </div>
      </div>

      <Tabs defaultValue="excel" className="space-y-4">
        <TabsList>
          <TabsTrigger value="excel" data-testid="tab-excel">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Import Excel
          </TabsTrigger>
          <TabsTrigger value="automations" data-testid="tab-automations">
            <Mail className="h-4 w-4 mr-1.5" /> Automazioni Email
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <ListChecks className="h-4 w-4 mr-1.5" /> Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="excel"><ExcelImportTab /></TabsContent>
        <TabsContent value="automations"><AutomationsTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
